import { useState, useRef } from 'react';
import axios from 'axios';
import { X, Upload, FileText, BookUser, CheckCircle2, AlertTriangle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const opts = { withCredentials: true };

const TABS = [
  {
    id: 'csv',
    label: 'CSV',
    icon: FileText,
    accept: '.csv,text/csv',
    endpoint: '/crm/contacts/import-csv',
    hint: "Colonnes reconnues : prenom/first_name, nom/last_name, email, phone/tel, company/société, role, status, notes (séparateurs , ou ;).",
  },
  {
    id: 'vcard',
    label: 'vCard (.vcf)',
    icon: BookUser,
    accept: '.vcf,text/vcard',
    endpoint: '/crm/contacts/import-vcard',
    hint: 'Fichier .vcf exporté depuis Apple Contacts, Google Contacts, Outlook, etc.',
  },
];

const CrmImportDialog = ({ onClose, onImported }) => {
  const [tabId, setTabId] = useState('csv');
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);

  const tab = TABS.find((t) => t.id === tabId);

  const submit = async () => {
    if (!file) {
      toast.error('Sélectionnez un fichier');
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(`${API}${tab.endpoint}`, fd, opts);
      setResult(r.data);
      const c = r.data.created_count || 0;
      toast.success(`${c} contact${c > 1 ? 's' : ''} importé${c > 1 ? 's' : ''}`);
      if (onImported) onImported();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Échec import');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="crm-import-dialog"
      className="fixed inset-0 z-[300] flex items-center justify-center p-4"
      style={{ background: 'rgba(6,8,12,0.82)', backdropFilter: 'blur(10px)' }}
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-lg rounded-2xl overflow-hidden bg-[#0E1116]"
        style={{ border: '1px solid rgba(255,255,255,0.10)', boxShadow: '0 30px 80px -20px rgba(0,0,0,0.85)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <header
          className="px-5 py-4 flex items-center justify-between"
          style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}
        >
          <div className="flex items-center gap-2.5">
            <span
              className="w-9 h-9 rounded-xl flex items-center justify-center"
              style={{ background: 'rgba(217,160,91,0.16)', border: '1px solid rgba(217,160,91,0.36)' }}
            >
              <Upload className="w-4 h-4" style={{ color: '#D9A05B' }} />
            </span>
            <div>
              <p className="text-[10px] uppercase tracking-[0.22em] font-bold" style={{ color: '#D9A05B' }}>
                Mini-CRM
              </p>
              <p className="text-base font-semibold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Importer des contacts
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="crm-import-close"
            aria-label="Fermer"
            className="rounded-lg p-1.5 transition-colors text-white/55 hover:text-[#D9A05B]"
          >
            <X className="w-4 h-4" />
          </button>
        </header>

        <div className="p-5 space-y-4">
          <div role="tablist" className="flex gap-1 bg-white/[0.04] border border-white/10 p-1 rounded-lg w-fit">
            {TABS.map((t) => {
              const Icon = t.icon;
              const isActive = t.id === tabId;
              return (
                <button
                  key={t.id}
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => { setTabId(t.id); setFile(null); setResult(null); }}
                  data-testid={`crm-import-tab-${t.id}`}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors flex items-center gap-1.5 ${
                    isActive ? 'bg-[#D9A05B]/15 text-[#E8B26C] border border-[#D9A05B]/30' : 'text-white/55 border border-transparent'
                  }`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {t.label}
                </button>
              );
            })}
          </div>

          <p className="text-[12px] text-white/55 leading-relaxed">{tab.hint}</p>

          <div
            className="rounded-xl p-4 cursor-pointer transition-colors"
            style={{
              background: 'rgba(255,255,255,0.03)',
              border: `1px dashed ${file ? 'rgba(217,160,91,0.45)' : 'rgba(255,255,255,0.15)'}`,
            }}
            onClick={() => inputRef.current?.click()}
            data-testid="crm-import-dropzone"
          >
            <input
              ref={inputRef}
              type="file"
              accept={tab.accept}
              className="hidden"
              data-testid="crm-import-file-input"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
            />
            <div className="flex items-center gap-3">
              <div
                className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
                style={{ background: 'rgba(217,160,91,0.10)', border: '1px solid rgba(217,160,91,0.20)' }}
              >
                <Upload className="w-4 h-4" style={{ color: '#D9A05B' }} />
              </div>
              <div className="flex-1 min-w-0">
                {file ? (
                  <>
                    <p className="text-[13px] text-white font-medium truncate">{file.name}</p>
                    <p className="text-[11px] text-white/50">{(file.size / 1024).toFixed(1)} Ko</p>
                  </>
                ) : (
                  <>
                    <p className="text-[13px] text-white/80">Cliquez pour sélectionner un fichier</p>
                    <p className="text-[11px] text-white/45">Max 5 Mo · UTF-8</p>
                  </>
                )}
              </div>
            </div>
          </div>

          {result && (
            <div className="rounded-xl border border-white/10 bg-white/[0.04] p-3 space-y-2" data-testid="crm-import-result">
              <div className="grid grid-cols-3 gap-2 text-center">
                <div>
                  <p className="text-[9px] uppercase tracking-[0.2em] text-emerald-400/80 mb-0.5">Créés</p>
                  <p className="text-2xl font-semibold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {result.created_count}
                  </p>
                </div>
                <div>
                  <p className="text-[9px] uppercase tracking-[0.2em] text-amber-400/80 mb-0.5">Doublons</p>
                  <p className="text-2xl font-semibold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {result.skipped_count}
                  </p>
                </div>
                <div>
                  <p className="text-[9px] uppercase tracking-[0.2em] text-red-400/80 mb-0.5">Erreurs</p>
                  <p className="text-2xl font-semibold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {result.error_count}
                  </p>
                </div>
              </div>
              {result.errors && result.errors.length > 0 && (
                <div className="text-[11px] text-white/55 max-h-24 overflow-y-auto pt-2 border-t border-white/5">
                  {result.errors.slice(0, 5).map((er, i) => (
                    <div key={i} className="flex items-start gap-1.5 py-0.5">
                      <AlertTriangle className="w-3 h-3 mt-0.5 text-amber-400/70 shrink-0" />
                      Ligne {er.row}: {er.error}
                    </div>
                  ))}
                  {result.errors.length > 5 && (
                    <p className="text-white/40 italic mt-1">+ {result.errors.length - 5} autres…</p>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="flex items-center justify-end gap-2 pt-1">
            <Button variant="ghost" onClick={onClose} data-testid="crm-import-cancel">
              {result ? 'Fermer' : 'Annuler'}
            </Button>
            <Button
              onClick={submit}
              disabled={!file || loading}
              data-testid="crm-import-submit"
              className="bg-[#D9A05B] hover:bg-[#C78D46] text-black"
            >
              {loading ? (
                <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Import…</>
              ) : result ? (
                <><CheckCircle2 className="w-4 h-4 mr-1.5" /> Re-importer</>
              ) : (
                <><Upload className="w-4 h-4 mr-1.5" /> Lancer l'import</>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CrmImportDialog;
